import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import _ from "lodash";

import { DescriptionIcon, DownloadOutlinedIcon, LaunchIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { openURLInNewTab } from "utils/common";
import { downloadFileWithFileName } from "utils/file";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Tag from "components/Tag";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import MemoESignatureEmailAssigneeBox from "entities/Memo/components/Sections/MemoESignatureDrawer/components/MemoESignatureEmailAssigneeBox";
import MemoESignatureTrackingProcessTag from "entities/Memo/components/Sections/MemoESignatureDrawer/components/MemoESignatureProcessTrackingTag";
import MemoESignatureReminder from "entities/Memo/components/Sections/MemoESignatureDrawer/components/MemoESignatureReminder";
import MemoESignatureUserAssigneeBox from "entities/Memo/components/Sections/MemoESignatureDrawer/components/MemoESignatureUserAssigneeBox";
import {
  INITIAL_VALUES,
  MemoSignatureStatus,
  VALIDATION_SCHEMA,
} from "entities/Memo/components/Sections/MemoESignatureDrawer/constants";
import { getOrdinalSuffix } from "entities/Memo/components/Sections/MemoESignatureDrawer/utils";
import {
  IMemoSignatureRoleProcessTrackingData,
  ISnapshotESingProcessTrackingData,
  memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve,
  memoPDFSignedDocumentFileDataWithSignedUrlRetrieve,
  memoSignatureProcessCancel,
  memoSignatureReminderToggle,
  memoSignatureReminderTrigger,
  memoSignatureReminderUpdate,
  useMemoDealDetails,
} from "entities/Memo/sdk";

export interface IMemoESignatureProcessTrackingTable {
  isLoading: boolean;
  dealId: number;
  memoId: number;
  roles: Array<IMemoSignatureRoleProcessTrackingData>;
  snapshot: ISnapshotESingProcessTrackingData;
  onClose: () => void;
  onUpdate: () => void;
}

const MemoESignatureProcessTrackingTable = ({
  dealId,
  memoId,
  roles,
  isLoading,
  snapshot,
  onClose,
  onUpdate,
}: IMemoESignatureProcessTrackingTable) => {
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const { mutate: mutateMemoDealDetails } = useMemoDealDetails({
    dealId,
    memoId,
  });

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isReminderUpdateProcessing, setIsReminderUpdateProcessing] =
    useState<boolean>(false);

  const handleSubmit = useCallback(
    ({ days_interval_time: intervalTime }: { days_interval_time: number }) => {
      setIsReminderUpdateProcessing(true);
      memoSignatureReminderUpdate({
        intervalTime,
        dealId,
        memoId,
      })
        .then(() => {
          toast.successMessage(
            "You have successfully updated the time interval for the automated reminders!"
          );
          onUpdate();
        })
        .catch(handleInvalidRequest)
        .finally(() => setIsReminderUpdateProcessing(false));
    },
    [dealId, memoId, onUpdate]
  );

  const currentProcessIntervalTime = useMemo(
    () =>
      roles.flatMap((role) =>
        role.memo_signature_fields.flatMap((field) =>
          field.signatures
            .filter(
              (signature) => signature.status === MemoSignatureStatus.PENDING
            )
            .flatMap((signature) => Number(signature.days_interval_time))
        )
      ),
    [roles]
  );

  const currentProcessEnabledReminders = useMemo(
    () =>
      roles.flatMap((role) =>
        role.memo_signature_fields.flatMap((field) =>
          Boolean(_.first(field.signatures)?.reminders_enabled)
        )
      ),
    [roles]
  );

  const formik = useFormik({
    initialValues: {
      ...INITIAL_VALUES,
      days_interval_time: currentProcessIntervalTime[0],
      reminders_enabled: currentProcessEnabledReminders[0],
    },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const onIntervalTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue("days_interval_time", event.target.value);
    },
    [formik]
  );

  const isProcessCompleted = useMemo(
    () => !_.isNil(snapshot.signed_file),
    [snapshot.signed_file]
  );

  const onDownload = useCallback(() => {
    setIsDownloading(true);
    const toastId = toast.loading("Retrieving file...");

    if (!isProcessCompleted) {
      memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve({
        dealId,
        memoId,
        memoPDFId: snapshot.id,
      }).then((data) => {
        downloadFileWithFileName({
          fileUrl: data.url,
          fileName: data.original_file_name,
          toastId,
        })
          .catch(handleInvalidRequest)
          .finally(() => setIsDownloading(false));
      });
    } else {
      memoPDFSignedDocumentFileDataWithSignedUrlRetrieve({
        memoId,
        dealId,
        memoPDFId: snapshot.id,
      }).then((data) =>
        downloadFileWithFileName({
          fileUrl: data.url,
          fileName: data.original_file_name,
          toastId,
        })
          .catch(handleInvalidRequest)
          .finally(() => setIsDownloading(false))
      );
    }
  }, [dealId, memoId, snapshot, isProcessCompleted]);

  const handleMemoPDFFilePreview = useCallback(() => {
    if (!isProcessCompleted) {
      memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve({
        dealId,
        memoId,
        memoPDFId: snapshot.id,
      }).then(({ url }) => {
        openURLInNewTab({ url });
      });
    } else {
      memoPDFSignedDocumentFileDataWithSignedUrlRetrieve({
        memoId,
        dealId,
        memoPDFId: snapshot.id,
      }).then(({ url }) => openURLInNewTab({ url }));
    }
  }, [dealId, memoId, snapshot, isProcessCompleted]);

  const onProcessCancel = useCallback(
    () =>
      memoSignatureProcessCancel({ memoId, dealId })
        .then(() => {
          mutateMemoDealDetails();
          toast.successMessage("E-signature process canceled successfully!");
          onClose();
        })
        .catch(handleInvalidRequest),
    [dealId, memoId, mutateMemoDealDetails, onClose]
  );

  const onReminderTrigger = useCallback(
    ({ roleId }: { roleId: number }) =>
      memoSignatureReminderTrigger({ roleId, memoId, dealId })
        .then(() => {
          toast.successMessage(
            "A new notification email has been successfully sent!"
          );
        })
        .catch(handleInvalidRequest),
    [dealId, memoId]
  );

  const handleCancel = useCallback(() => {
    showConfirmationDialog({
      onConfirm: onProcessCancel,
      confirmButtonText: "Proceed",
      message:
        "Are you sure you want to cancel the ongoing e-signature process?",
    });
  }, [showConfirmationDialog, onProcessCancel]);

  const handleReminderTrigger = useCallback(
    ({ roleId }: { roleId: number }) => {
      showConfirmationDialog({
        onConfirm: () => onReminderTrigger({ roleId }),
        confirmButtonText: "Proceed",
        message: "Are you sure you want to manually trigger the reminder?",
      });
    },
    [showConfirmationDialog, onReminderTrigger]
  );

  const areAllSignaturesCollected = useMemo(
    () =>
      roles.every((role) =>
        role.memo_signature_fields.every((field) =>
          field.signatures.every(
            (signature) => signature.status === MemoSignatureStatus.APPLIED
          )
        )
      ),
    [roles]
  );

  const isProcessRejected = useMemo(
    () =>
      roles.some((role) =>
        role.memo_signature_fields.some((field) =>
          field.signatures.some(
            (signature) => signature.status === MemoSignatureStatus.REJECTED
          )
        )
      ),
    [roles]
  );

  const onRemindersProcessStatusChange = useCallback(
    ({ remindersEnabled }: { remindersEnabled: boolean }) => {
      memoSignatureReminderToggle({
        dealId,
        memoId,
        remindersEnabled,
      })
        .then(() => {
          toast.successMessage(
            `Reminders have been successfully ${remindersEnabled ? "enabled" : "disabled"}. `
          );
          formik.setFieldValue("reminders_enabled", remindersEnabled);
          onUpdate();
        })
        .catch(handleInvalidRequest);
    },
    [dealId, memoId, formik, onUpdate]
  );

  return (
    <Stack>
      <Stack
        direction="row"
        borderBottom={`1px solid ${colors.blue60}`}
        paddingBottom={0.5}
        gap={2}
      >
        {!_.isNil(memoId) && !_.isNil(dealId) && (
          <Text variant="labelSmall" flexBasis="40px">
            order
          </Text>
        )}
        <Text variant="labelSmall" flexBasis="260px">
          Role
        </Text>
        <Text variant="labelSmall">Contact assigned & STATUS</Text>
      </Stack>
      {isLoading &&
        _.range(5).map((index) => (
          <Skeleton key={index} height={83} width="100%" />
        ))}
      {!isLoading &&
        roles.map((role) => {
          const currentField = _.first(
            role.memo_signature_fields.filter(
              (field) => field.signatures.length > 0
            )
          );

          return (
            !_.isNil(currentField) && (
              <Stack
                direction="row"
                borderBottom={`1px solid ${colors.blue60}`}
                paddingBottom={1.5}
                marginTop={1}
                gap={2}
              >
                <Tooltip
                  title={getOrdinalSuffix({
                    number: currentField.order,
                  })}
                >
                  <Stack
                    gap={1}
                    flexBasis="40px"
                    alignItems="flex-start"
                    height="fit-content"
                  >
                    <Tag
                      label={getOrdinalSuffix({
                        number: currentField.order,
                      })}
                      variant="gray60"
                      sx={{
                        padding: theme.spacing(0.25, 1),
                        maxWidth: "40px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    />
                  </Stack>
                </Tooltip>
                <Tooltip
                  title={getOrdinalSuffix({
                    number: currentField.order,
                  })}
                  placement="bottom-start"
                >
                  <Stack
                    gap={1}
                    flexBasis="260px"
                    alignItems="flex-start"
                    height="fit-content"
                  >
                    <Tag
                      label={role.name}
                      variant="gray100"
                      sx={{
                        padding: theme.spacing(0.25, 1),
                        maxWidth: "260px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    />
                  </Stack>
                </Tooltip>
                <Stack sx={{ flexGrow: 1 }}>
                  {currentField.signatures.map((signature) => (
                    <>
                      {!_.isNil(signature.email) && (
                        <MemoESignatureEmailAssigneeBox
                          key={signature.email}
                          email={signature.email as string}
                        />
                      )}
                      {!_.isNil(signature.user) && _.isNil(signature.email) && (
                        <MemoESignatureUserAssigneeBox
                          key={signature.user?.id}
                          user={signature.user}
                        />
                      )}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        paddingTop={1}
                      >
                        <MemoESignatureTrackingProcessTag
                          status={signature.status}
                          signedAt={signature.signed_at}
                        />
                        {signature.status === MemoSignatureStatus.PENDING && (
                          <Tooltip
                            title={
                              !formik.values.reminders_enabled
                                ? "The reminders for this e-sign process have been disabled"
                                : undefined
                            }
                          >
                            <span>
                              <Button
                                onClick={() =>
                                  handleReminderTrigger({ roleId: role.id })
                                }
                                sx={{
                                  alignSelf: "flex-end",
                                  justifySelf: "flex-end",
                                  padding: theme.spacing(0.2, 1),
                                  height: "fit-content",
                                }}
                                variant="outlined"
                                color="secondary"
                                disabled={!formik.values.reminders_enabled}
                              >
                                <Text variant="text4" fontWeight={500}>
                                  Send reminder
                                </Text>
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </>
                  ))}
                </Stack>
              </Stack>
            )
          );
        })}
      <Stack marginTop={4}>
        {!areAllSignaturesCollected && !isProcessRejected && (
          <MemoESignatureReminder
            formik={formik}
            onIntervalTimeChange={onIntervalTimeChange}
            disableSubmit={isReminderUpdateProcessing}
            onRemindersProcessStatusChange={onRemindersProcessStatusChange}
          />
        )}
        <Stack gap={2}>
          <Stack
            gap={0.5}
            sx={{
              padding: theme.spacing(1.5, 1.5),
              backgroundColor: colors.gray10,
              borderRadius: "8px",
            }}
          >
            <Text variant="text2" fontWeight={500}>
              {isProcessCompleted
                ? "Download a signed copy of the original document."
                : "View or download the original document sent out for signing"}
            </Text>
            <Text variant="text4" color={colors.gray80}>
              {isProcessCompleted
                ? "The signed document has been sent out to all signatory parties."
                : "The signed document will be sent to all signatory parties once all signatures have been collected."}
            </Text>
            <Stack
              direction="row"
              justifyContent="space-between"
              marginTop={1.5}
              paddingY={2}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <DescriptionIcon sx={{ width: "24px", heigth: "24px" }} />
                <Stack direction="row" gap={1.5}>
                  <Tooltip title={snapshot.file.original_file_name}>
                    <Text
                      variant="text4"
                      fontWeight={700}
                      noWrap
                      overflow="hidden"
                      textOverflow="ellipsis"
                      maxWidth={700}
                    >
                      {snapshot.file.original_file_name}
                    </Text>
                  </Tooltip>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{
                    padding: theme.spacing(0.2, 1),
                    height: "fit-content",
                  }}
                  onClick={handleMemoPDFFilePreview}
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <LaunchIcon
                      sx={{
                        width: 16,
                        height: 16,
                      }}
                    />
                    <Text variant="text4" fontWeight={500}>
                      View
                    </Text>
                  </Stack>
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={isDownloading}
                  sx={{
                    padding: theme.spacing(0.2, 1),
                    height: "fit-content",
                  }}
                  onClick={onDownload}
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <DownloadOutlinedIcon
                      sx={{
                        width: 16,
                        height: 16,
                      }}
                    />
                    <Text variant="text4" fontWeight={500}>
                      Download
                    </Text>
                  </Stack>
                </Button>
              </Stack>
            </Stack>
          </Stack>

          {!areAllSignaturesCollected && (
            <Stack
              gap={0.5}
              alignItems="flex-start"
              sx={{
                padding: 1.5,
                backgroundColor: colors.gray10,
                borderRadius: "8px",
              }}
            >
              <Text variant="text2" fontWeight={500}>
                No longer need this document signed?
              </Text>
              <Text variant="text4" color={colors.gray80}>
                Cancelling the process will destroy the current document and
                render all signatures null. The signatory parties will be
                notified that the process has been halted. You can go back to
                editing your document, assigning new signatories, and sending it
                out for signing.
              </Text>
              <Button
                color="error"
                sx={{ alignSelf: "flex-end", marginTop: 1 }}
              >
                <Text variant="text3" fontWeight={500} onClick={handleCancel}>
                  Cancel ongoing e-sign process
                </Text>
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default MemoESignatureProcessTrackingTable;
